import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center font-bold uppercase tracking-[0.3em] transition-all disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default:
          "bg-studio-accent text-studio-dark hover:scale-105 hover:bg-white shadow-xl",
        secondary:
          "bg-studio-panel text-studio-accent border border-studio-accent/20 hover:bg-studio-accent/10",
        outline:
          "border border-studio-accent/40 text-studio-primary bg-transparent hover:bg-studio-accent/10",
        ghost:
          "text-studio-secondary hover:text-studio-primary hover:bg-white/5",
        destructive:
          "bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30",
      },
      size: {
        default: "h-16 px-14 text-[10px] rounded-full",
        sm: "h-10 px-6 text-[9px] rounded-full",
        lg: "h-20 px-20 text-[11px] rounded-full",
        icon: "h-10 w-10 rounded-full text-[10px]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
